import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Mail, Menu, X, LogOut, LayoutDashboard, User, ShoppingCart, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { COMPANY } from '../mock';
import { useAuth } from '../context/AuthContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useSite } from '../context/SiteContext';
import api, { mediaUrl, asArray } from '../lib/api';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Products', to: '/products', hasMenu: true },
  { label: 'Projects', to: '/projects' },
  { label: 'Partners', to: '/partners' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', to: '/contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);
  const [productMenu, setProductMenu] = useState([]);
  const { user, logout, isAdmin } = useAuth();
  const { count, setOpen: setCartOpen } = useQuoteCart();
  const { settings } = useSite();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/products/menu').then((res) => setProductMenu(asArray(res.data))).catch(() => {});
  }, []);
  useEffect(() => { setOpen(false); setMenuOpen(false); }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  const CartButton = ({ className = '' }) => (
    <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className={`relative grid place-items-center w-10 h-10 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors ${className}`} aria-label="Open enquiry cart">
      <ShoppingCart className="w-[18px] h-[18px]" />
      {count > 0 && (
        <span data-testid="header-cart-count" className="absolute -top-1.5 -right-1.5 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-primary to-accent text-white text-[10px] font-bold">{count}</span>
      )}
    </button>
  );

  return (
    <header className="relative z-50 bg-[#070a12] border-b border-white/10">
      <div className="hidden md:block bg-black/40 text-gray-400 text-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${COMPANY.phones[0]}`} className="flex items-center gap-2 hover:text-white transition-colors"><Phone className="w-3.5 h-3.5 text-primary" /> {COMPANY.phones[0]}</a>
            <a href={`mailto:${COMPANY.emails[0]}`} className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="w-3.5 h-3.5 text-primary" /> {COMPANY.emails[0]}</a>
          </div>
          <div className="flex items-center gap-6">
            <a href={settings?.employeeMailUrl || 'https://mail.google.com'} target="_blank" rel="noopener noreferrer" data-testid="employee-login-link" className="flex items-center gap-1.5 hover:text-white transition-colors font-heading font-semibold"><User className="w-3.5 h-3.5 text-primary" /> Employee Login</a>
            <div className="font-display tracking-[0.25em] fire-text font-bold">SAFETY FIRST, FLAMES LAST</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[74px]">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
          <img src={mediaUrl(settings?.logoUrl) || '/closefire-logo.png'} alt="Closefire" className="h-16 w-16 object-contain drop-shadow-[0_0_12px_rgba(232,51,42,0.35)]" />
          <span className="font-display text-2xl font-extrabold tracking-wide text-white hidden sm:inline">CLOSEFIRE</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV.map((n) => (
            n.hasMenu ? (
              <div key={n.to} className="relative" onMouseEnter={() => setMenuOpen(true)} onMouseLeave={() => setMenuOpen(false)}>
                <Link to={n.to} data-testid="nav-products" className={`relative flex items-center gap-1 px-3 py-2 font-heading font-semibold text-[14px] transition-colors ${isActive(n.to) ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
                  {n.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                  {isActive(n.to) && <motion.span layoutId="navdot" className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1.5 h-1.5 rounded-full bg-primary" />}
                </Link>
                <AnimatePresence>
                  {menuOpen && productMenu.length > 0 && (
                    <motion.div
                      data-testid="products-mega-menu"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[720px] max-w-[90vw]">
                      <div className="rounded-2xl bg-[#0b0f1a] border border-white/10 shadow-2xl shadow-black/60 p-6 grid grid-cols-3 gap-6">
                        {productMenu.map((group) => (
                          <div key={group.category}>
                            <Link to="/products" className="block font-heading font-extrabold text-primary text-[15px] mb-3 hover:text-accent transition-colors">{group.category}</Link>
                            <ul className="space-y-1.5">
                              {asArray(group.items).map((it) => (
                                <li key={it.id}>
                                  <Link to={`/product/${it.id}`} data-testid={`mega-menu-item-${it.id}`} className="block text-[13.5px] text-gray-400 hover:text-white hover:translate-x-1 transition-all">{it.name}</Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={n.to} to={n.to} className={`relative px-3 py-2 font-heading font-semibold text-[14px] transition-colors ${isActive(n.to) ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
                {n.label}
                {isActive(n.to) && <motion.span layoutId="navdot" className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1.5 h-1.5 rounded-full bg-primary" />}
              </Link>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <CartButton />
          {user ? (
            <div className="flex items-center gap-3">
              {isAdmin && <Button variant="outline" onClick={() => navigate('/admin')} className="gap-2 bg-white/5 border-white/15 text-white hover:bg-white/10"><LayoutDashboard className="w-4 h-4" /> Dashboard</Button>}
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-white/10 text-white"><User className="w-4 h-4" /></span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-white">{user.name.split(' ')[0]}</div>
                  <button onClick={logout} className="text-xs text-gray-400 hover:text-primary flex items-center gap-1"><LogOut className="w-3 h-3" /> Logout</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <Button onClick={() => navigate('/contact')} className="gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/30">Get a Quote</Button>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <CartButton />
          <button className="text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}</button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden border-t border-white/10 bg-[#070a12]/95 backdrop-blur-xl">
            <nav className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                n.hasMenu ? (
                  <div key={n.to}>
                    <button onClick={() => setMobileProducts((v) => !v)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg font-heading font-semibold ${isActive(n.to) ? 'text-white bg-white/10' : 'text-gray-300'}`}>
                      {n.label} <ChevronDown className={`w-4 h-4 transition-transform ${mobileProducts ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {mobileProducts && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pl-3">
                          {productMenu.map((group) => (
                            <div key={group.category} className="py-2">
                              <div className="text-xs font-heading font-bold text-primary uppercase tracking-wide px-3">{group.category}</div>
                              {asArray(group.items).map((it) => (
                                <Link key={it.id} to={`/product/${it.id}`} className="block px-3 py-1.5 text-sm text-gray-400">{it.name}</Link>
                              ))}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link key={n.to} to={n.to} className={`px-3 py-2.5 rounded-lg font-heading font-semibold ${isActive(n.to) ? 'text-white bg-white/10' : 'text-gray-300'}`}>{n.label}</Link>
                )
              ))}
              <div className="h-px bg-white/10 my-2" />
              <a href={settings?.employeeMailUrl || 'https://mail.google.com'} target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 rounded-lg font-heading font-semibold text-gray-300 flex items-center gap-2"><User className="w-4 h-4 text-primary" /> Employee Login</a>
              {user ? (
                <>
                  {isAdmin && <Link to="/admin" className="px-3 py-2.5 rounded-lg font-heading font-semibold text-white">Dashboard</Link>}
                  <button onClick={logout} className="text-left px-3 py-2.5 rounded-lg font-heading font-semibold text-primary">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/register" className="px-3 py-2.5 rounded-lg font-heading font-semibold text-gray-300">Register</Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
